import {
  Firestore,
  WithRef,
  getError,
  snapshotCombineLatest,
  toNamedDocument,
} from '@principle-theorem/shared';
import {
  BaseSteriliserDestinationEntity,
  ISterilisationMachineJobData,
  ISterilisationMachineRecordMigrationData,
} from '../../../destination/entities/sterilisation-machine';
import {
  D4WSterilisationMachine,
  SterilisationMachineSourceEntity,
} from '../../source/entities/sterilisation-machines';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IPracticeMigration,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { ISterilisationMachine } from '@principle-theorem/principle-core/interfaces';
import { TranslationMapHandler } from '../../../translation-map';
import { compact } from 'lodash';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { Observable, combineLatest, of } from 'rxjs';
import { PracticeMigration } from '../../../practice-migrations';
import { map, switchMap } from 'rxjs/operators';
import { D4WSterilisationMachineMappingHandler } from '../mappings/sterilisation-machine';

interface ID4WSteriliserJobData
  extends ISterilisationMachineJobData<D4WSterilisationMachine> {
  sourceSterilisationMachine: IGetRecordResponse<D4WSterilisationMachine>;
}

export class D4WSterilisationMachineDestinationEntity extends BaseSteriliserDestinationEntity<D4WSterilisationMachine> {
  sterilisationMachineSourceEntity = new SterilisationMachineSourceEntity();
  sterilisationMachineCustomMapping =
    new D4WSterilisationMachineMappingHandler();

  override sourceEntities = {
    sterilisationMachines: new SterilisationMachineSourceEntity(),
  };

  customMappings = {
    sterilisationMachines: this.sterilisationMachineCustomMapping,
    practices: new D4WPracticeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<ISterilisationMachineJobData<D4WSterilisationMachine>[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const sterilisationMachineMappings$ =
      this.sterilisationMachineCustomMapping.getRecords(translationMap);

    return snapshotCombineLatest([
      practices$,
      brand$,
      sterilisationMachineMappings$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sterilisationMachineSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourceRecords, [practices, brand, sterilisationMachineMappings]]) =>
        sourceRecords.map((sourceSterilisationMachine) => ({
          sourceSterilisationMachine,
          practices,
          brand,
          sterilisationMachineMappings,
        }))
      )
    );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: ID4WSteriliserJobData
  ): Promise<
    | ISterilisationMachineRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourceMachineId = this.sterilisationMachineSourceEntity
        .getSourceRecordId(data.sourceSterilisationMachine.data)
        .toString();
      const sourceSterilisationMachine = data.sourceSterilisationMachine.data;

      const description = compact([
        sourceSterilisationMachine.make,
        sourceSterilisationMachine.model,
        sourceSterilisationMachine.serial,
        sourceSterilisationMachine.notes,
      ])
        .join(' ')
        .trim();

      const practiceSourceId = sourceSterilisationMachine.practiceId.toString();
      const practiceMap = data.practices.find(
        (practice) => practice.sourceIdentifier === practiceSourceId
      );

      if (!practiceMap?.destinationIdentifier) {
        throw new Error(`No practice with id ${practiceSourceId}`);
      }
      const practice = await Firestore.getDoc(
        practiceMap.destinationIdentifier
      );

      const machine: ISterilisationMachine = {
        id: sourceMachineId,
        name: data.sourceSterilisationMachine.record.label,
        description,
        practice: toNamedDocument(practice),
        deleted: !sourceSterilisationMachine.isActive,
      };

      return {
        sterilisationMachineRecord: {
          ...machine,
          sourceIdentifier: sourceMachineId,
          createdAt:
            data.sourceSterilisationMachine.record.translations.createdAt,
        },
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceSterilisationMachine,
        getError(error)
      );
    }
  }

  buildSkippedResponse(
    record: ID4WSteriliserJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: record.sourceSterilisationMachine.record.uid,
      label: record.sourceSterilisationMachine.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: record.sourceSterilisationMachine.record.id,
    };
  }
}
